<script lang="ts" setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import OmniDialog from "@/components/omni/OmniDialog.vue";
import { useFilters, useOrderPayments } from "@/composables";

import type { OrderPayment } from "@/api/orders/dto/order.dto";

const { t } = useI18n();
const { mdash } = useFilters();
const { isLoading, deleteOrderPayment, formatOrderPaymentRate } = useOrderPayments();

const showDialog = defineModel<boolean>({ default: false });

const props = defineProps<{
  orderId: string;
  payment: OrderPayment | null;
}>();

const emit = defineEmits<{
  (e: "success", payment: OrderPayment): void;
}>();

const confirmationText = computed<string>(() => {
  if (!props.payment) {
    return "";
  }

  return t("order-payments.delete-confirmation", {
    type: props.payment.type ? t(`order-payments.${props.payment.type}`) : mdash(null),
    rate: props.payment.rate === null ? mdash(null) : formatOrderPaymentRate(props.payment.rate),
  });
});

function close() {
  showDialog.value = false;
}

async function removePayment() {
  if (!props.payment?.id) {
    return;
  }

  const isDeleted = await deleteOrderPayment(props.orderId, props.payment.id);

  if (!isDeleted) {
    return;
  }

  emit("success", props.payment);
  close();
}
</script>

<template>
  <omni-dialog
    v-model="showDialog"
    :primary-action-label="t('general.delete')"
    :primary-button-loading="isLoading"
    :secondary-action-label="t('general.cancel')"
    :secondary-button-disabled="isLoading"
    :title="t('payments.delete-payment')"
    @primary-action:click="removePayment"
    @secondary-action:click="close"
  >
    <div class="text-grey-darken-3">{{ confirmationText }}</div>
  </omni-dialog>
</template>
